import { Fragment, useMemo, useState, useEffect, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'
import { Button, Loader } from '../../components'
import { reassignCycleCounts } from '../../services/cycleCountServices'
import { OperatorSelectTable } from './OperatorSelectTable'
import { localizeReviewError, stripCodePrefix } from './reviewErrors'
import { chunk } from './events/collectUnassignedIds'

const T_PREFIX = 'cycle_counts.reassign_modal'
const EVENTS_T_PREFIX = 'cycle_counts.events'

const REVIEW_STATES = ['submitted', 'review_pending', 'review_submitted']
const REVIEW_PICKER_ROLES = ['ROLE_OPERATOR', 'ROLE_INVENTORY_REVIEWER']

const collectCycleCountIds = batches => {
  const ids = []
  batches.forEach(batch => {
    const locations = batch?.locations || []
    locations.forEach(loc => {
      if (loc?.id != null) ids.push(loc.id)
    })
  })
  return [...new Set(ids)]
}

const collectCounterNames = batches => {
  const names = new Set()
  batches.forEach(batch => (batch?.locations || []).forEach(loc => loc?.counter?.full_name && names.add(loc.counter.full_name)))
  return [...names]
}

// batch.locations only holds the group's first 100 rows (see locations_truncated); a counter
// present only in the truncated tail is missed here and must be caught by the server instead.
const collectCounterIds = batches => {
  const ids = new Set()
  batches.forEach(batch => (batch?.locations || []).forEach(loc => loc?.counter?.id != null && ids.add(loc.counter.id)))
  return [...ids]
}

// Maps the numeric cycle-count ids the server lists after "cycle counts" in the
// REVIEWER_IS_COUNTER message to the location codes the operator recognizes, for
// interpolation into the translated error string (see reviewErrors.js). Returns
// null when the marker is missing or no ids follow it — the message shape has
// changed underneath us, so the caller should show the raw message rather than
// render the translated string with a dangling empty list.
const extractLocationCodes = (message, batches) => {
  const marker = 'cycle counts'
  const markerIndex = message.toLowerCase().indexOf(marker)
  if (markerIndex === -1) return null

  const codeById = {}
  batches.forEach(batch =>
    (batch?.locations || []).forEach(loc => {
      if (loc?.id != null && loc?.location_code != null) codeById[loc.id] = loc.location_code
    }),
  )
  const tail = message.slice(markerIndex + marker.length)
  const ids = tail.match(/\d+/g) || []
  if (ids.length === 0) return null
  return ids.map(id => codeById[id] ?? id).join(', ')
}

export const ReassignCycleCountDialog = ({ open, warehouseId, selectedBatches = [], resolveCycleCountIds, onSuccess = () => {}, onCancel = () => {} }) => {
  const { i18n } = useTranslation()
  const [mode, setMode] = useState('single')
  const [selectedOperator, setSelectedOperator] = useState(null)
  const [selectedOperators, setSelectedOperators] = useState([])
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  // Pool mode (resolveCycleCountIds provided): the row shown only carries its first 100
  // locations, so the real id list comes from walking the paged pool endpoint instead.
  const [poolIds, setPoolIds] = useState(null)
  const [poolLoading, setPoolLoading] = useState(false)
  const [poolFallback, setPoolFallback] = useState(false)
  const [poolError, setPoolError] = useState(null)
  const [chunkProgress, setChunkProgress] = useState(null)
  // Guards every setState that follows an await against firing after the dialog
  // component itself unmounts (a route change while a pool walk or chunk is in flight).
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const mapSubmitError = error => {
    const err = error.response?.data?.error
    const errorCode = error.response?.data?.error_code
    const rawMsg = typeof err === 'string' ? err : err?.message || i18n.t(`${T_PREFIX}.error`)
    const isReviewerIsCounterError = errorCode ? errorCode === 'REVIEWER_IS_COUNTER' : /REVIEWER_IS_COUNTER/.test(rawMsg)
    const locations = isReviewerIsCounterError ? extractLocationCodes(rawMsg, selectedBatches) : null
    // The server changed the message shape underneath us (no "cycle counts"
    // marker, or no id after it) — show the raw message instead of the
    // translated string with an empty, dangling location list.
    return isReviewerIsCounterError && locations == null ? stripCodePrefix(rawMsg) : localizeReviewError(rawMsg, errorCode, { locations })
  }

  useEffect(() => {
    if (!open || !resolveCycleCountIds) {
      setPoolIds(null)
      setPoolLoading(false)
      setPoolFallback(false)
      setPoolError(null)
      return
    }
    let cancelled = false
    setPoolLoading(true)
    setPoolFallback(false)
    setPoolError(null)
    resolveCycleCountIds()
      .then(ids => {
        if (cancelled) return
        setPoolIds([...new Set(ids)])
        setPoolLoading(false)
      })
      .catch(error => {
        if (cancelled) return
        // Only an older platform without the pool endpoint falls back to what the
        // capped row already shows — any other failure has no safe id list to use.
        if (error?.response?.status === 404) {
          setPoolIds(collectCycleCountIds(selectedBatches))
          setPoolFallback(true)
        } else {
          setPoolError(mapSubmitError(error))
        }
        setPoolLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resolveCycleCountIds])

  const cycleCountIds = useMemo(
    () => (resolveCycleCountIds ? (poolIds ?? []) : collectCycleCountIds(selectedBatches)),
    [resolveCycleCountIds, poolIds, selectedBatches],
  )

  const selectionStatus = useMemo(() => {
    if (selectedBatches.length === 0) return null
    // An unassigned batch has no operator yet, so it needs the same
    // operator-picker flow as a pending one — normalize it here so a
    // pending+unassigned selection isn't flagged as mixed.
    const statuses = new Set(selectedBatches.map(b => (b?.status === 'unassigned' ? 'pending' : b?.status)))
    if (statuses.size > 1) return 'mixed'
    return [...statuses][0]
  }, [selectedBatches])

  const isReviewState = REVIEW_STATES.includes(selectionStatus)
  const isMixed = selectionStatus === 'mixed'
  const counterNames = useMemo(() => (isReviewState ? collectCounterNames(selectedBatches) : []), [isReviewState, selectedBatches])
  const counterIds = useMemo(() => (isReviewState ? collectCounterIds(selectedBatches) : []), [isReviewState, selectedBatches])

  useEffect(() => {
    if (!open) {
      setMode('single')
      setSelectedOperator(null)
      setSelectedOperators([])
      setBusy(false)
      setErrorMessage(null)
      setChunkProgress(null)
    }
  }, [open])

  useEffect(() => {
    if (isReviewState && mode !== 'single') {
      setMode('single')
    }
  }, [isReviewState, mode])

  const canSubmit = useMemo(() => {
    if (resolveCycleCountIds && (poolLoading || poolError)) return false
    if (cycleCountIds.length === 0 || isMixed) return false
    if (mode === 'single') return !!selectedOperator
    return selectedOperators.length > 0
  }, [resolveCycleCountIds, poolLoading, poolError, cycleCountIds.length, isMixed, mode, selectedOperator, selectedOperators])

  const buildPayload = ids => {
    const payload = { warehouse_id: warehouseId, cycle_count_ids: ids }
    if (mode === 'single') payload.assigned_to_id = selectedOperator.id
    else payload.assigned_to_ids = selectedOperators.map(op => op.id)
    return payload
  }

  // The pool can be larger than the server's per-request cap, so it's sent as sequential
  // chunks. A chunk that fails stops the loop but keeps whatever already succeeded — the
  // caller still refetches so the lists reflect that partial progress. The dialog closes
  // right after onSuccess fires (see the container), so a partial failure is reported
  // THROUGH onSuccess rather than left in local state that would never render.
  const submitPool = async () => {
    const chunks = chunk(cycleCountIds, 500)
    let doneIds = 0
    let totalReassigned = 0
    for (const ids of chunks) {
      if (!mountedRef.current) return
      setChunkProgress({ done: doneIds, total: cycleCountIds.length })
      try {
        const result = await reassignCycleCounts(buildPayload(ids))
        if (!mountedRef.current) return
        totalReassigned += result?.reassigned_count ?? 0
        doneIds += ids.length
      } catch (error) {
        if (!mountedRef.current) return
        setBusy(false)
        setChunkProgress(null)
        const message = `${i18n.t(`${EVENTS_T_PREFIX}.pool.partial`, { done: doneIds, total: cycleCountIds.length })} ${mapSubmitError(error)}`
        onSuccess({ reassigned_count: totalReassigned, partial: true, message })
        return
      }
    }
    if (!mountedRef.current) return
    setBusy(false)
    setChunkProgress(null)
    onSuccess({ reassigned_count: totalReassigned })
  }

  const onSubmit = async () => {
    if (!canSubmit) return
    setBusy(true)
    setErrorMessage(null)
    // An assigned batch is rendered whole (never truncated) and a take-over can make it larger
    // than the server's 500-id cap, so anything above the cap also goes through the chunked path.
    if (resolveCycleCountIds || cycleCountIds.length > 500) {
      await submitPool()
      return
    }
    try {
      const result = await reassignCycleCounts(buildPayload(cycleCountIds))
      setBusy(false)
      onSuccess(result)
    } catch (error) {
      setBusy(false)
      setErrorMessage(mapSubmitError(error))
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => {}}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-5xl sm:w-full p-6 text-gray-600 my-3 overflow-hidden max-h-[length:90vh]">
              <Loader show={busy || poolLoading} />
              {!busy && (
                <div className="absolute top-0 right-0 p-5 cursor-pointer" onClick={onCancel}>
                  <XIcon className="h-5 w-5" />
                </div>
              )}

              <Dialog.Title className="text-lg text-center mb-2">{i18n.t(`${T_PREFIX}.${isReviewState ? 'review_title' : 'title'}`)}</Dialog.Title>

              <p className="text-sm text-center text-gray-500 mb-4">
                {resolveCycleCountIds
                  ? i18n.t(`${EVENTS_T_PREFIX}.pool.scope`, { count: cycleCountIds.length })
                  : isReviewState
                    ? i18n.t(`${T_PREFIX}.review_summary`, { locations: cycleCountIds.length })
                    : i18n.t(`${T_PREFIX}.summary`, { batches: selectedBatches.length, locations: cycleCountIds.length })}
              </p>

              {resolveCycleCountIds && poolLoading && <p className="text-xs text-center text-gray-500 mb-3">{i18n.t(`${EVENTS_T_PREFIX}.pool.resolving`)}</p>}

              {resolveCycleCountIds && poolFallback && (
                <p className="text-xs text-center text-amber-700 mb-3">{i18n.t(`${EVENTS_T_PREFIX}.pool.fallback`, { count: cycleCountIds.length })}</p>
              )}

              {resolveCycleCountIds && poolError && <p className="text-sm text-red-600 mb-3 text-center">{poolError}</p>}

              {busy && chunkProgress && (
                <p className="text-xs text-center text-gray-500 mb-3">{i18n.t(`${EVENTS_T_PREFIX}.pool.assigning_chunks`, chunkProgress)}</p>
              )}

              {isReviewState && counterNames.length > 0 && (
                <p className="text-xs text-center text-amber-700 mb-3">{i18n.t(`${T_PREFIX}.counter_notice`, { names: counterNames.join(', ') })}</p>
              )}

              {isMixed && <p className="text-sm text-red-600 mb-3 text-center">{i18n.t(`${T_PREFIX}.mixed_status_error`)}</p>}

              {!isMixed && !isReviewState && (
                <div className="flex justify-center gap-2 mb-4">
                  <button
                    type="button"
                    className={`px-3 py-1 text-sm rounded ${mode === 'single' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    onClick={() => setMode('single')}
                  >
                    {i18n.t(`${T_PREFIX}.mode_single`)}
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-sm rounded ${mode === 'distribute' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    onClick={() => setMode('distribute')}
                  >
                    {i18n.t(`${T_PREFIX}.mode_distribute`)}
                  </button>
                </div>
              )}

              {!isMixed && (
                <>
                  {mode === 'single' ? (
                    <OperatorSelectTable
                      selectionType="radio"
                      selectedOperator={selectedOperator}
                      onOperatorSelect={setSelectedOperator}
                      warehouseId={warehouseId}
                      disabledOperatorIds={isReviewState ? counterIds : undefined}
                      showPendingCountsColumn={!isReviewState}
                      {...(isReviewState ? { roles: REVIEW_PICKER_ROLES } : { role: 'ROLE_OPERATOR' })}
                    />
                  ) : (
                    <OperatorSelectTable
                      selectionType="checkbox"
                      selectedOperators={selectedOperators}
                      onOperatorsChange={setSelectedOperators}
                      warehouseId={warehouseId}
                      role="ROLE_OPERATOR"
                    />
                  )}
                </>
              )}

              {errorMessage && <p className="text-sm text-red-600 mt-3 text-center">{errorMessage}</p>}

              <div className="mt-3 flex justify-between">
                <Button className="m-1" type="secondary" onClick={onCancel} disabled={busy}>
                  {i18n.t(`${T_PREFIX}.cancel`)}
                </Button>
                <Button className="m-1" type="primary" disabled={!canSubmit || busy} onClick={onSubmit} loading={busy}>
                  {i18n.t(`${T_PREFIX}.confirm`)}
                </Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
